'use client';

import { useEffect, useState } from 'react';
import {
  Poll,
  Quiz,
  Predictor,
  Leaderboard,
} from '@stadionhq-v3/gamification-react';
import { useSession } from 'next-auth/react';
import { isGamificationEnabled } from '@providers/gamification/gamification.flags';
import styles from './gamificationEntry.module.scss';

// Shape of the Contentful `gamificationEntry` JSON (Object) field, e.g.
// `{ "id": "quiz-fac3…", "type": "Quiz" }`.
export type GamificationEntryJson = {
  id: string;
  type: string;
};

export type RenderGamificationEntryProps = {
  gamificationEntry?: GamificationEntryJson | null;
  /**
   * Layout size. 'sm' locks the gamification UI to its single-column layout —
   * used inside the narrow match-centre commentary column. 'lg' (default) uses
   * the components' own responsive breakpoints.
   */
  size?: 'sm' | 'lg';
};

type GamificationKind = 'poll' | 'quiz' | 'predictor' | 'leaderboard';

const KINDS: GamificationKind[] = ['poll', 'quiz', 'predictor', 'leaderboard'];

// The `gamificationEntry` JSON is a free-form Object field authored by hand, so
// the `type` casing isn't guaranteed (`Quiz` vs `quiz`). Resolve case-insensitively
// and fall back to the id prefix (ids are `quiz-…`, `poll-…`, `predictor-…`).
const resolveKind = (
  type?: string,
  id?: string
): GamificationKind | undefined => {
  const fromType = type?.trim().toLowerCase();
  if (fromType && KINDS.includes(fromType as GamificationKind)) {
    return fromType as GamificationKind;
  }
  const fromId = id?.trim().toLowerCase().split('-')[0];
  return KINDS.find((kind) => kind === fromId);
};

const RenderGamificationEntry = ({
  gamificationEntry,
  size = 'lg',
}: RenderGamificationEntryProps) => {
  const id = gamificationEntry?.id;
  const kind = resolveKind(gamificationEntry?.type, id);

  // The SDK components fire a one-shot fetch on mount that branches on whether an
  // axios `Authorization` header is set *at that instant*: with a token they call
  // `/…/with-submission` (returning the signed-in user's existing answer), without
  // one they call the plain endpoint (no submission). On a fresh page load the
  // session token isn't on axios yet — NextAuth's `useSession` is still `loading`,
  // and even once resolved `GamificationProvider` attaches the header in a
  // `useEffect` that runs *after* this child's mount fetch. So the first fetch
  // would go out unauthenticated and the predictor/poll state machine would latch
  // its "not yet answered" state with no recovery once the token arrives (its
  // `question` state ignores the later `AUTH_SUCCESS`). Defer mounting by a tick
  // until the session has resolved and the provider has configured the header, so
  // the first fetch is authenticated and an existing prediction survives a refresh.
  const { status } = useSession();
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    if (status !== 'loading') setAuthReady(true);
  }, [status]);

  // Feature flag: this website must not render gamification unless explicitly
  // enabled. CMS entries stay authored against fixtures/articles for the native
  // app, but every placement renders through here — so this gate disables them
  // all. (Placed after the hooks above to keep hook order unconditional.)
  if (!isGamificationEnabled) return null;

  if (!id || !kind) return null;
  if (!authReady) return <div className={styles.gamificationEntry} aria-busy />;

  // The gamification UI components share `gamificationId` + `onDark` (+ `size`
  // for Poll/Quiz/Predictor). We dispatch explicitly (rather than a component
  // map) to keep each call type-safe against its own prop type.
  let entry = null;
  switch (kind) {
    case 'poll':
      entry = <Poll gamificationId={id} onDark size={size} />;
      break;
    case 'quiz':
      entry = <Quiz gamificationId={id} onDark size={size} />;
      break;
    case 'predictor':
      entry = <Predictor gamificationId={id} onDark size={size} />;
      break;
    case 'leaderboard':
      entry = <Leaderboard gamificationId={id} onDark />;
      break;
    default:
      return null;
  }

  return (
    <div
      className={styles.gamificationEntry}
      data-gamification-entry
      data-size={size}
    >
      {entry}
    </div>
  );
};

export default RenderGamificationEntry;
